import { createFileRoute, Navigate, Outlet, redirect } from "@tanstack/react-router";
import { useAuth0 } from "@auth0/auth0-react";
import type { RouterContext } from "~/router";
import { useAuth0Login, type AuthUser } from "~/hooks/useAuth0Login";
import { Spinner } from "~/components/ui/spinner";
import { useAuthenticatedUser } from "~/hooks/useAuthenticatedUser";
import { EmailVerificationGate } from "~/components/email-verification-gate";

export const Route = createFileRoute("/_protected")({
  beforeLoad: async ({ context, location }) => {
    const { auth } = context as RouterContext;
    if (!auth.isLoading) {
      if (!auth.isAuthenticated) {
        // Preserve invitation query params when redirecting to login
        const searchParams = new URLSearchParams(location.search);
        const invitation = searchParams.get("invitation");
        const organization = searchParams.get("organization");
        const organization_name = searchParams.get("organization_name");

        throw redirect({
          to: "/login",
          search: {
            ...(invitation && { invitation }),
            ...(organization && { organization }),
            ...(organization_name && { organization_name }),
          },
        });
      }

    }
  },
  component: ProtectedLayout,
});

function ProtectedLayout() {
  const { isLoading, isAuthenticated } = useAuth0Login();
  const { user: auth0User } = useAuth0<AuthUser>();
  const {
    user,
    routing,
    isLoading: isUserLoading,
    isUniversityAccount,
  } = useAuthenticatedUser();

  if (isLoading || (isAuthenticated && isUserLoading)) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <Spinner className="size-8 text-brand-green-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <Spinner className="size-8 text-brand-green-primary" />
      </div>
    );
  }

  // Block access until email is verified
  if (auth0User && !auth0User.email_verified) {
    return <EmailVerificationGate />;
  }

  if (isUniversityAccount) {
    return <Navigate to={(routing?.homePath ?? "/dashboard")} replace />;
  }

  // If a personal account user has a university-domain email, redirect them
  // to their university portal. This catches users who signed up via /signup
  // instead of their university's portal.
  if (user?.universityRedirect) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-6 p-8 bg-gray-50">
        <h1 className="text-2xl font-bold text-black">University Portal Available</h1>
        <p className="text-black text-center max-w-md">
          Your email address belongs to <strong>{user.universityRedirect.universityName}</strong>,
          which has a dedicated portal on Proudly Human.
          Please continue through your university&apos;s portal.
        </p>
        <a
          href={`/${user.universityRedirect.tenantSlug}/login`}
          className="inline-flex items-center justify-center rounded-md bg-brand-green-primary px-6 py-2.5 text-sm font-medium text-white hover:bg-brand-green-primary/90 transition-colors"
        >
          Continue to {user.universityRedirect.universityName} Portal
        </a>
      </div>
    );
  }

  return <Outlet />;
}
